import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { wardService, WardListFilters } from '../wardService';

export function useWards(page = 1, limit = 20, filters?: WardListFilters) {
  return useQuery({
    queryKey: ['wards', page, limit, filters],
    queryFn: () => wardService.getWards(page, limit, filters),
    staleTime: 15000,
    placeholderData: (previous) => previous,
  });
}

export function useWard(id: string) {
  return useQuery({
    queryKey: ['wards', id],
    queryFn: () => wardService.getWard(id),
    enabled: !!id,
  });
}

export function useWardBeds(id: string) {
  return useQuery({
    queryKey: ['wards', id, 'beds'],
    queryFn: () => wardService.getWardBeds(id),
    enabled: !!id,
  });
}

export function useCreateWard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: wardService.createWard,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wards'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUpdateWard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => wardService.updateWard(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['wards', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['wards'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useDeleteWard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: wardService.deleteWard,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wards'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useBulkDeleteWards() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => wardService.bulkDeleteWards(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wards'] });
      queryClient.invalidateQueries({ queryKey: ['beds'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useOccupancySummary() {
  return useQuery({
    queryKey: ['wards', 'occupancy'],
    queryFn: () => wardService.getOccupancySummary(),
    staleTime: 30000,
  });
}
